import React from "react";
import { themeClasses } from "../../utils";
import { SegmentedProgressBarProps } from "./segmentedProgressBarTypes";

export const SegmentedProgressBar: React.FC<SegmentedProgressBarProps> = ({
  segments,
  height = "h-8",
  showPercentages = false,
  minPercentageToShow = 0,
  activeSegmentKey,
  segmentColors,
  defaultColor = themeClasses.bgSurface400,
  inactiveColor = themeClasses.bgGray400,
  activeColor,
  className = "",
  ariaLabel = "Progress bar",
  markerPercentage,
  valueNow,
}) => {
  const getSegmentColor = (segmentKey: string) => {
    if (activeSegmentKey) {
      // If there's an active segment, only highlight it, others get inactive color
      return segmentKey === activeSegmentKey
        ? activeColor || segmentColors?.[segmentKey] || defaultColor
        : inactiveColor;
    }

    // No active segment, use normal colors
    return segmentColors?.[segmentKey] || defaultColor;
  };

  const getTextColor = (bgColor: string) => {
    // For dark backgrounds, use light text
    const darkBackgrounds = [
      themeClasses.bgIndigoDataviz1,
      themeClasses.bgIndigoDataviz2,
      themeClasses.bgIndigoDataviz3,
      themeClasses.bgIndigoDataviz4,
      themeClasses.bgIndigoDataviz5,
    ];

    return darkBackgrounds.includes(bgColor as any)
      ? themeClasses.textSurface0
      : themeClasses.textSurface950;
  };

  return (
    <div
      className={`relative rounded-lg overflow-hidden flex ${height} ${className}`}
      role="progressbar"
      aria-label={ariaLabel}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={valueNow ?? segments.reduce((sum, segment) => sum + segment.value, 0)}
    >
      {segments.map(segment => {
        const bgColor = getSegmentColor(segment.key);
        const shouldShowPercentage = showPercentages && segment.value > minPercentageToShow;
        const textColor = shouldShowPercentage ? getTextColor(bgColor) : "";

        return (
          <div
            key={segment.key}
            style={{ width: `${segment.value}%` }}
            className={`flex items-center justify-center text-sm font-medium ${bgColor} ${textColor}`}
            aria-label={segment.label || `${segment.value}%`}
          >
            {shouldShowPercentage && `${segment.value}%`}
          </div>
        );
      })}
      {markerPercentage != null && (
        <div
          aria-hidden="true"
          className={`absolute inset-y-0 w-0.5 ${themeClasses.bgSurface950}`}
          style={{ left: `calc(${markerPercentage}% - 1px)` }}
        />
      )}
    </div>
  );
};
